/**
 * Helpers to defensively unwrap the project's inconsistent API response shapes.
 *
 * Across endpoints the payload may arrive as any of:
 *   - `[...]`                          (bare array)
 *   - `{ data: [...] }`                (data array)
 *   - `{ data: { content: [...] } }`   (paginated)
 *   - `{ content: [...] }`             (paginated, already unwrapped)
 *
 * These helpers normalise those into a predictable value so call sites don't
 * each re-implement the same `Array.isArray ? … : .content ?? []` guards.
 */

/** Unwrap a list-shaped payload into a plain array (never null/undefined). */
export function extractList<T = any>(payload: any): T[] {
  if (Array.isArray(payload)) return payload;
  const d = payload?.data ?? payload;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.content)) return d.content;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

/** Unwrap a single-object payload (handles `{ data: {…} }` nesting). */
export function extractData<T = any>(payload: any): T | undefined {
  return payload?.data?.data ?? payload?.data ?? payload;
}
